import React from 'react';

import { useMatchMedia } from '@betterment/js-runtime';
import { Box, get as themeGet } from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import { dropRight as _dropRight, last as _last } from 'es-toolkit/compat';

import ActionMenu, { ActionMenuProps, Item } from './ActionMenu';

const defaultTrackingEventData = {
  module: 'HeaderActionMenu',
  name: 'MenuButton',
};

const MobileMenuButtonContainer = styled(Box)`
  position: fixed;
  right: 0;
  top: 0;

  [data-reach-menu] {
    position: fixed;
    right: 0;
    text-align: initial;
  }

  [data-reach-menu-popover] {
    right: 0px;
    min-width: 200px;
  }

  [data-reach-menu-button] {
    &:hover,
    &:focus {
      background-color: rgba(255, 255, 255, 0.4);
    }

    &:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.4);
    }
  }
`;

const SummaryMenuButtonContainer = styled(Box)<{ buttonHover?: string }>`
  button {
    font-weight: ${themeGet('fontWeights.medium')};

    &:hover {
      background-color: ${({ buttonHover = '' }) => buttonHover};
    }
  }
`;

const MobileVersion: React.FC<
  React.PropsWithChildren<{
    menuItems: ActionMenuProps['menuItems'];
    trackingEventData: ActionMenuProps['trackingEventData'];
  }>
> = ({ menuItems, trackingEventData }) => {
  return (
    <MobileMenuButtonContainer>
      <ActionMenu
        iconName="Transfer"
        menuItems={menuItems}
        variant="solidPrimary"
        css={{ padding: 'space.200', margin: 'space.0' }}
        trackingEventData={trackingEventData ?? defaultTrackingEventData}
      />
    </MobileMenuButtonContainer>
  );
};

type HeaderActionMenuComponent = React.FC<
  React.PropsWithChildren<
    Pick<ActionMenuProps, 'buttonText' | 'menuItems' | 'variant' | 'trackingEventData'> & {
      buttonHover?: string;
      isUserTimeOfDayEnabled?: boolean;
    }
  >
>;

const HeaderActionMenu: HeaderActionMenuComponent = ({
  buttonText,
  variant,
  menuItems = [],
  trackingEventData,
  buttonHover,
  isUserTimeOfDayEnabled,
}) => {
  const isAtLeastLarge = useMatchMedia('atLeastLarge');
  const menuItemsWithSeparator = _dropRight(menuItems, 1).concat({ separator: true }, _last(menuItems) as Item);

  if (isAtLeastLarge && isUserTimeOfDayEnabled) {
    return (
      <SummaryMenuButtonContainer buttonHover={buttonHover}>
        <ActionMenu
          buttonText={buttonText}
          menuItems={menuItemsWithSeparator}
          variant={variant}
          trackingEventData={trackingEventData ?? defaultTrackingEventData}
        />
      </SummaryMenuButtonContainer>
    );
  }

  return isAtLeastLarge ? (
    <ActionMenu
      buttonText={buttonText}
      menuItems={menuItemsWithSeparator}
      variant={variant}
      trackingEventData={trackingEventData ?? defaultTrackingEventData}
    />
  ) : (
    <MobileVersion
      menuItems={menuItemsWithSeparator}
      trackingEventData={trackingEventData ?? defaultTrackingEventData}
    />
  );
};

export default HeaderActionMenu;
